import axiosClient from '@/common/AxiosClient';
import { RequestActivityFeed } from '@/components/RequestActivityFeed';
import { TrainingRequestActivity } from '@/data/attributes/TrainingRequestActivity';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useCourseEnrollmentHistory } from '@/hooks/useCourseEnrollmentHistory';
import { useAppData } from '@/providers/AppDataProvider';
import { useLocale } from '@/providers/LocaleProvider';
import { useSnackbar } from '@/providers/SnackbarProvider';
import { onOpenChecklistPreviewDialog } from '@/store/checklist.slice';
import { KLAYO_COLORS } from '@/themes';
import { CourseEnrollmentHistoryEvent } from '@/types/views/learningHistory.types';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { SyntheticEvent, useMemo } from 'react';
import { LearningHistoryRow, LearningHistoryTable } from '../table/LearningHistoryTable';
import { Dialog } from './Dialog';

interface LearningHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Rendered as the dialog title. */
  studentName?: string;
  /** Rendered as the dialog content sub-title. */
  courseName?: string;
  /** Internal CourseEnrollment id. Null for non-local-cache rows — the table then shows empty. */
  courseEnrollmentId?: string | null;
  /** EmployeeTraining id for manual-training rows. When set, the dialog shows the request
   *  activity feed (approval timeline) instead of the enrolment history table. */
  employeeTrainingId?: string | null;
  /** LearningPathEnrollment id. When present the row is a learning path, and the enrolment-history
   *  query hits the learning-paths history endpoint instead of the courses one. */
  learningPathEnrollmentId?: string | null;
  /** IntegrationLearnUponEnrollment id for rows from the organisation's own LearnUpon portal. Their
   *  history is derived from the synced enrolment dates by the integrations history endpoint. */
  integrationEnrollmentId?: string | null;
  theme?: any;
}

// Most-recent first by default, matching the backend ordering (and the mock).
const DEFAULT_SORT: GridSortModel = [{ field: 'date', sort: 'desc' }];

const HISTORY_COLUMNS = [
  { type: LearningHistoryTable.columns.date },
  { type: LearningHistoryTable.columns.details },
];

export const LearningHistoryDialog = ({
  open,
  onClose,
  studentName,
  courseName,
  courseEnrollmentId,
  employeeTrainingId,
  learningPathEnrollmentId,
  integrationEnrollmentId,
  theme,
}: LearningHistoryDialogProps) => {
  const { translate } = useLocale();
  const { user } = useAppData();
  const { showSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  const isTrainingRequest = !!employeeTrainingId;

  // Enrolment-history table runs only for course enrolments; the manual-training path uses the
  // request activity feed instead. Learning-path rows hit the learning-paths history endpoint and
  // own-portal rows the integrations one — useCourseEnrollmentHistory picks the endpoint based on
  // which id is present.
  const historyEnrollmentId =
    learningPathEnrollmentId ?? courseEnrollmentId ?? integrationEnrollmentId;

  const { data, isPending } = useCourseEnrollmentHistory(
    { courseEnrollmentId, learningPathEnrollmentId, integrationEnrollmentId },
    open && !isTrainingRequest
  );

  const { data: activity, isLoading: isActivityLoading } = useAppQuery<any>(
    `/EmployeeTraining/GetActivityHistory/${employeeTrainingId}`,
    {
      enabled: open && isTrainingRequest,
      select: (res) => new TrainingRequestActivity(res),
      placeholderData: undefined,
    }
  );

  const onDocumentClick = async (_e: SyntheticEvent, doc: any) => {
    try {
      const response = await axiosClient.get(`/EmployeeTraining/download/${doc.documentId}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.documentName);
      document.body.appendChild(link);
      link.click();

      showSnackbar(translate('practicals.downloadedDocument', { name: doc.documentName }), 'info');
    } catch (error) {}
  };

  const onChecklistClick = (checklist: any) => {
    dispatch(onOpenChecklistPreviewDialog({ disabled: true, checklistData: checklist }));
  };

  const describeEvent = (e: CourseEnrollmentHistoryEvent): string => {
    switch (e.eventType) {
      case 'requested':
        return translate('manage.learningHistory.requested');
      case 'enrolled':
        return translate('manage.learningHistory.enrolled');
      case 'started':
        return translate('manage.learningHistory.started');
      case 'launched':
        return translate('manage.learningHistory.launched');
      case 'unenrolled':
        return translate('manage.learningHistory.unenrolled');
      case 'completed': {
        const score = e.percentage ?? e.percentageCompleted ?? null;
        const resultKey =
          e.rawStatus === 'passed' ? 'passed' : e.rawStatus === 'failed' ? 'failed' : 'completed';
        const result = translate(`manage.learningHistory.result.${resultKey}`);
        return score != null
          ? translate('manage.learningHistory.completedWithScore', { result, score })
          : translate('manage.learningHistory.completed');
      }
      default:
        return '';
    }
  };

  const rows = useMemo<LearningHistoryRow[]>(
    () =>
      (data?.events ?? []).map((event, i) => {
        const detail = describeEvent(event);
        return {
          id: `${event.eventType}-${event.eventDateUtc}-${i}`,
          date: event.eventDateUtc,
          // LP history merges several courses, so label each event with its source course.
          details: event.courseName
            ? translate('manage.learningHistory.eventWithCourse', {
                detail,
                course: event.courseName,
              })
            : detail,
        };
      }),
    // describeEvent only depends on translate; data drives the rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, translate]
  );

  // Query only runs with a real id; treat a missing id as "no history" rather than perpetual load.
  const isLoading = !!historyEnrollmentId && isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      theme={theme}
      fullWidth
      maxWidth='md'
      title={studentName}
      closeButtonSx={{ display: 'block', color: KLAYO_COLORS.NeonBlueNB500 }}
      contentSx={{
        paddingBottom: { xs: '20px !important', md: '40px !important' },
      }}
    >
      <Stack gap={3}>
        <Typography variant='h6' fontWeight={600} color={KLAYO_COLORS.GreyGR900} fontSize={18}>
          {courseName}
        </Typography>

        {isTrainingRequest ? (
          isActivityLoading ? (
            <Stack
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: {
                  xs: 3,
                  md: 5,
                },
              }}
            >
              <CircularProgress />
            </Stack>
          ) : activity ? (
            <RequestActivityFeed
              user={user}
              activity={activity}
              onDocumentClick={onDocumentClick}
              onChecklistClick={onChecklistClick}
              theme={theme}
            />
          ) : (
            <Typography color={KLAYO_COLORS.GreyGR500}>
              {translate('manage.learningHistory.empty')}
            </Typography>
          )
        ) : (
          <LearningHistoryTable
            rows={rows}
            columns={HISTORY_COLUMNS}
            theme={theme}
            translate={translate}
            paper={false}
            pagination={false}
            showFooter={false}
            selectable={false}
            sortModel={DEFAULT_SORT}
            isLoading={isLoading}
            noRowsMessage={translate('manage.learningHistory.empty')}
          />
        )}
      </Stack>
    </Dialog>
  );
};
